import { ChangeEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  withStyles,
  GoogleAddressField,
  AccountType,
  ActivateDeactiveteClientAccountRequest,
  ActivateDeactiveteContactRequest,
  AddressComponents,
  NoFillButton,
} from 'vopay';

import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import { useBlockingLoader } from '#shared/providers/Loader';
import useEditClientAccount from '../hooks/useEditClientAccount';
import useEditContact from '../hooks/useEditContact';
import useAccountDetailsTab from './useAccountDetailsTab';
import EditDetailItem from './EditDetailItem';
import { EditCustomerFields } from './types';
import styles from './DetailsTab.module.scss';

import { Button, Title } from '#shared/components/UI';

const cn = withStyles(styles);

export type AccountDetails = {
  Address1?: string;
  BusinessCategory?: string;
  BusinessIndustry?: string;
  BusinessNumber?: string;
  BusinessWebsite?: string;
  City?: string;
  AccountID?: string;
  AccountName?: string;
  Country?: string;
  DOB?: string;
  EmailAddress?: string;
  FirstName?: string;
  IsActive?: boolean;
  LastName?: string;
  PhoneNumber?: string;
  PostalCode?: string;
  Province?: string;
  SINLastDigits?: string;
  IsBusiness?: boolean;
  ClientType?: AccountType;
  AdditionalContacts?: string[];
};

type DetailsTabProps = {
  account: AccountDetails;
  onUpdate: () => Promise<unknown>;
  // Host fact the portal read from its ClientData provider.
  clientAccountsEnabled: boolean;
};

// The portal's Customer Details tab, ported for the contact drawer with the
// same field editing and activate/deactivate flows.
export default function DetailsTab({ account, onUpdate, clientAccountsEnabled }: DetailsTabProps) {
  const { accountTypeLabel, activateAccount, deactivateAccount } = useAccountDetailsTab(clientAccountsEnabled);
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const isClientAccount = clientAccountsEnabled;
  const accountIDType = accountTypeLabel === 'Client Account' ? 'ClientAccountID' : 'ContactID';
  const { editClientAccount } = useEditClientAccount();
  const { editContact } = useEditContact();
  const [activeRow, setActiveRow] = useState('');
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.DetailsTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });

  async function handleDialogue() {
    const result = await alertDialog.caution(
      t('Deactivate.confirmationTitle', {
        defaultValue: `Deactivate  ${accountTypeLabel}`,
        accountTypeLabel,
      }),
      t('Deactivate.confirmationContent', {
        defaultValue: `${accountTypeLabel} cannot be used in transactions or other activities until it is reactivated. Any in progress activity will be cancelled`,
        accountTypeLabel,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Deactivate.deactivate', { defaultValue: 'Deactivate' }),
      },
    );

    if (result === 'confirm') {
      blockingLoader.show();

      try {
        await deactivateAccount({
          [accountIDType]: account.AccountID,
        } as ActivateDeactiveteClientAccountRequest | ActivateDeactiveteContactRequest);

        toast.neutral(
          t('Deactivate.success', {
            defaultValue: `${account.AccountID} Deactivated`,
            accountID: account.AccountID,
          }),
        );

        onUpdate();
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('unknownError', { defaultValue: 'Unknown error' });

        await alertDialog.caution(
          t('Deactivate.errorTitle', {
            defaultValue: `Unable to Deactivate ${accountTypeLabel}`,
            accountTypeLabel,
          }),
          msg,
          {
            confirmLabel: tGlobal('Buttons.okay', { defaultValue: 'Okay' }),
          },
        );
      } finally {
        blockingLoader.hide();
      }
    }
  }

  async function handleActivateClick() {
    blockingLoader.show();

    try {
      await activateAccount({
        [accountIDType]: account.AccountID,
      } as ActivateDeactiveteClientAccountRequest | ActivateDeactiveteContactRequest);

      toast.success(
        t('Activate.success', {
          defaultValue: `${account.AccountID} Activated`,
          accountID: account.AccountID,
        }),
      );

      onUpdate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('unknownError', { defaultValue: 'Unknown error' });

      await alertDialog.caution(
        t('Activate.error', {
          defaultValue: `Unable to Activate ${accountTypeLabel}`,
          accountType: accountTypeLabel,
        }),
        msg,
        {
          confirmLabel: tGlobal('Buttons.okay', { defaultValue: 'Okay' }),
        },
      );
    } finally {
      blockingLoader.hide();
    }
  }

  const submitEditContact = async (rowName: string, values: Partial<EditCustomerFields>) => {
    try {
      blockingLoader.show();

      if (isClientAccount) {
        await editClientAccount({
          ...values,
          ClientAccountID: account.AccountID || '',
        });
      } else {
        await editContact({
          ...values,
          ContactID: account.AccountID || '',
          EmailAddress: account.EmailAddress || '',
          PhoneNumber: account.PhoneNumber || '',
          FirstName: values.FirstName || '',
          LastName: values.LastName || '',
        });
      }

      await onUpdate();

      const clientType = isClientAccount
        ? t('clientAccount', { defaultValue: 'Client Account' })
        : t('contact', { defaultValue: 'Contact' });

      toast.success(
        t('Edit.success', {
          defaultValue: `${clientType} ${rowName} Updated`,
          clientType,
          rowName,
        }),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('error', { defaultValue: 'Error' }), msg, {
        abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
      });
    } finally {
      setActiveRow('');
      blockingLoader.hide();
    }
  };

  function getDisabledClass(rowName?: string) {
    if (activeRow === '') {
      return '';
    }

    return activeRow !== rowName ? 'disabled' : '';
  }

  const [addressFields, setAddressFields] = useState({
    Address1: account.Address1,
    City: account.City,
    Province: account.Province,
    PostalCode: account.PostalCode,
    Country: account.Country,
  });

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setAddressFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoogleAddressSelect = (addressComponents: AddressComponents) => {
    setAddressFields({
      Address1: addressComponents.address,
      City: addressComponents.city,
      Province: addressComponents.province,
      PostalCode: addressComponents.postalcode,
      Country: addressComponents.country,
    });
  };

  const addressFieldList = [
    { id: 'city', name: 'City', label: t('city', { defaultValue: 'City' }) },
    { id: 'province', name: 'Province', label: t('province', { defaultValue: 'Province' }) },
    { id: 'postal-code', name: 'PostalCode', label: t('postalCode', { defaultValue: 'Postal Code' }) },
    { id: 'country', name: 'Country', label: t('country', { defaultValue: 'Country' }) },
  ];

  return (
    <div className={cn('detailsTab')}>
      <Box flx="col" gapSize="10px">
        <Title level="5" className={cn('tabTitle', getDisabledClass())}>
          {t('pageTitle', {
            defaultValue: `${accountTypeLabel} Details`,
            accountTypeLabel,
          })}
        </Title>

        <Card style={{ padding: '10px 12px' }}>
          <Row
            label={t('accountType', {
              defaultValue: `${accountTypeLabel} Type`,
              accountType: accountTypeLabel,
            })}
            className={cn(getDisabledClass())}
          >
            {account.IsBusiness
              ? t('business', { defaultValue: 'Business' })
              : t('individual', { defaultValue: 'Individual' })}
          </Row>

          <Row
            label={t('accountID', {
              defaultValue: `${accountTypeLabel} ID`,
              accountType: accountTypeLabel,
            })}
            className={cn(getDisabledClass())}
          >
            {account.AccountID}
          </Row>
        </Card>

        <Card style={{ padding: '10px 12px' }}>
          {!account.IsBusiness && (
            <>
              <Row
                className={cn(getDisabledClass('accountName'))}
                label={t('firstLastName', { defaultValue: 'First & Last Name' })}
              >
                <EditDetailItem
                  value={account.AccountName || ''}
                  onActivate={() => {
                    setActiveRow('accountName');
                  }}
                  onCancel={() => setActiveRow('')}
                  onUpdate={(vals) => {
                    return submitEditContact(t('firstLastName', { defaultValue: 'First & Last Name' }), {
                      FirstName: vals.FirstName,
                      LastName: vals.LastName,
                    });
                  }}
                >
                  <div className={cn('multipleInputs')}>
                    <Box flx="col" gapSize="2px">
                      <label className={cn('inputLabel')} htmlFor="first-name">
                        {t('firstName', { defaultValue: 'First Name' })}
                      </label>

                      <input id="first-name" autoFocus name="FirstName" defaultValue={account.FirstName} />
                    </Box>

                    <Box flx="col" gapSize="2px">
                      <label className={cn('inputLabel')} htmlFor="last-name">
                        {t('lastName', { defaultValue: 'Last Name' })}
                      </label>

                      <input id="last-name" name="LastName" defaultValue={account.LastName} />
                    </Box>
                  </div>
                </EditDetailItem>
              </Row>

              <Row className={cn(getDisabledClass())} label={t('email', { defaultValue: 'Email Address' })}>
                {account.EmailAddress || ''}
              </Row>

              <Row className={cn(getDisabledClass())} label={t('phone', { defaultValue: 'Phone Number' })}>
                {formatPhoneNumber(account.PhoneNumber || '')}
              </Row>

              <Row className={cn(getDisabledClass())} label={t('dob', { defaultValue: 'Date of Birth' })}>
                {account.DOB || ''}
              </Row>

              {!!account.SINLastDigits && (
                <Row className={cn(getDisabledClass())} label={t('sin', { defaultValue: 'SIN/SSN - Last 4 Digits' })}>
                  {account.SINLastDigits || ''}
                </Row>
              )}
            </>
          )}

          {account.IsBusiness && (
            <>
              <Row
                className={cn(getDisabledClass('companyName'))}
                label={t('companyName', { defaultValue: 'Company Name' })}
              >
                <EditDetailItem
                  value={account.AccountName || ''}
                  onActivate={() => setActiveRow('companyName')}
                  onCancel={() => setActiveRow('')}
                  onUpdate={(vals) => {
                    // BusinessName because it's what the b/e expects
                    return submitEditContact(t('companyName', { defaultValue: 'Company Name' }), {
                      BusinessName: vals.BusinessName,
                    });
                  }}
                >
                  <Box flx="col" gapSize="2px">
                    <label className={cn('inputLabel')} htmlFor="business-name">
                      {t('companyName', { defaultValue: 'Company Name' })}
                    </label>

                    <input name="BusinessName" defaultValue={account.AccountName} autoFocus />
                  </Box>
                </EditDetailItem>
              </Row>

              <Row
                className={cn(getDisabledClass())}
                label={t('businessPhone', { defaultValue: 'Business Phone Number' })}
              >
                {formatPhoneNumber(account.PhoneNumber || '')}
              </Row>

              {account.ClientType === 'business' && (
                <>
                  <Row
                    className={cn(getDisabledClass('businessNumber'))}
                    label={t('businessNumber', { defaultValue: 'Business Number' })}
                  >
                    {isClientAccount ? (
                      <EditDetailItem
                        value={account.BusinessNumber || ''}
                        onActivate={() => setActiveRow('businessNumber')}
                        onCancel={() => setActiveRow('')}
                        onUpdate={(e) => {
                          return submitEditContact(t('businessNumber', { defaultValue: 'Business Number' }), {
                            BusinessNumber: e.BusinessNumber,
                          });
                        }}
                      >
                        <Box flx="col" gapSize="2px">
                          <label className={cn('inputLabel')} htmlFor="business-number">
                            {t('businessNumber', { defaultValue: 'Business Number' })}
                          </label>

                          <input name="BusinessNumber" defaultValue={account.BusinessNumber} autoFocus />
                        </Box>
                      </EditDetailItem>
                    ) : (
                      account.BusinessNumber || ''
                    )}
                  </Row>

                  <Row
                    className={cn(getDisabledClass('website'))}
                    label={t('businessWebsite', { defaultValue: 'Business Website' })}
                  >
                    {isClientAccount ? (
                      <EditDetailItem
                        value={account.BusinessWebsite || ''}
                        onActivate={() => setActiveRow('website')}
                        onCancel={() => setActiveRow('')}
                        onUpdate={(e) => {
                          return submitEditContact(t('businessWebsite', { defaultValue: 'Business Website' }), {
                            BusinessWebsite: e.BusinessWebsite,
                          });
                        }}
                      >
                        <Box flx="col" gapSize="2px">
                          <label className={cn('inputLabel')} htmlFor="business-website">
                            {t('businessWebsite', { defaultValue: 'Business Website' })}
                          </label>

                          <input name="BusinessWebsite" defaultValue={account.BusinessWebsite} autoFocus />
                        </Box>
                      </EditDetailItem>
                    ) : (
                      account.BusinessWebsite || ''
                    )}
                  </Row>
                </>
              )}

              <Row className={cn(getDisabledClass())} label={t('industry', { defaultValue: 'Industry' })}>
                {account.BusinessIndustry || ''}
              </Row>

              <Row className={cn(getDisabledClass())} label={t('category', { defaultValue: 'Category' })}>
                {account.BusinessCategory || ''}
              </Row>
            </>
          )}

          <Row className={cn(getDisabledClass('address'))} label={t('address', { defaultValue: 'Address' })}>
            <EditDetailItem
              value={[account.Address1, account.City, account.Province, account.PostalCode, account.Country]
                .filter((part) => part !== undefined && part !== '')
                .join(', ')}
              onActivate={() => setActiveRow('address')}
              onCancel={() => setActiveRow('')}
              onUpdate={(vals) => {
                if (!['ca', 'canada', 'us', 'united states'].includes(vals.Country.toLowerCase())) {
                  throw new Error(
                    t('Validations.country', {
                      defaultValue: 'Country must be either Canada or United States',
                    }),
                  );
                }

                return submitEditContact(t('address', { defaultValue: 'Address' }), {
                  Country: vals.Country,
                  Address1: vals.Address1,
                  City: vals.City,
                  Province: vals.Province,
                  PostalCode: vals.PostalCode,
                });
              }}
            >
              <div className={cn('multipleInputs')}>
                <Box flx="col" gapSize="2px">
                  <label className={cn('inputLabel')} htmlFor="address1">
                    {t('address1', { defaultValue: 'Address Line 1', defaultvalue: 'Address Line 1' })}
                  </label>

                  <GoogleAddressField
                    variant="none"
                    ISOCountry={['CA', 'US']}
                    name="Address1"
                    value={addressFields.Address1}
                    inputClass={styles.addressInput}
                    autoFocus
                    onChange={handleFieldChange}
                    onGoogleAddressSelect={handleGoogleAddressSelect}
                  />
                </Box>

                {addressFieldList.map(({ id, name, label }) => (
                  <Box key={id} flx="col" gapSize="2px">
                    <label className={cn('inputLabel')} htmlFor={id}>
                      {label}
                    </label>

                    <input
                      id={id}
                      name={name}
                      value={addressFields[name as keyof typeof addressFields]}
                      onChange={handleFieldChange}
                    />
                  </Box>
                ))}
              </div>
            </EditDetailItem>
          </Row>

          {account.AdditionalContacts && (
            <Row label={t('additionalEmail', { defaultValue: 'Additional Email Address' })}>
              <Box flx="col" gapSize="12px" ai="fe">
                {account.AdditionalContacts.map((contact) => (
                  <span key={contact}>{contact}</span>
                ))}
              </Box>
            </Row>
          )}
        </Card>

        {account.IsBusiness && (
          <Box flx="col" gapSize="10px">
            <Title level="5" className={cn('tabTitle', activeRow !== '' && 'disabled')}>
              {t('primaryContact', { defaultValue: 'Primary Contact' })}
            </Title>

            <Card style={{ padding: '10px 12px' }}>
              <Row
                className={cn(getDisabledClass('primaryContactName'))}
                label={t('firstLastName', { defaultValue: 'First & Last Name' })}
              >
                <EditDetailItem
                  value={[account.FirstName, account.LastName]
                    .filter((part) => part !== undefined && part !== '')
                    .join(' ')}
                  onActivate={() => setActiveRow('primaryContactName')}
                  onCancel={() => setActiveRow('')}
                  onUpdate={(vals) => {
                    return submitEditContact(t('firstLastName', { defaultValue: 'First & Last Name' }), {
                      FirstName: vals.FirstName,
                      LastName: vals.LastName,
                    });
                  }}
                >
                  <div className={cn('multipleInputs')}>
                    <Box flx="col" gapSize="2px">
                      <label className={cn('inputLabel')} htmlFor="first-name">
                        {t('firstName', { defaultValue: 'First Name' })}
                      </label>

                      <input id="first-name" autoFocus name="FirstName" defaultValue={account.FirstName} />
                    </Box>

                    <Box flx="col" gapSize="2px">
                      <label className={cn('inputLabel')} htmlFor="last-name">
                        {t('lastName', { defaultValue: 'Last Name' })}
                      </label>

                      <input id="last-name" name="LastName" defaultValue={account.LastName} />
                    </Box>
                  </div>
                </EditDetailItem>
              </Row>

              <Row label={t('email', { defaultValue: 'Email Address' })} className={cn(getDisabledClass())}>
                {account.EmailAddress || ''}
              </Row>
            </Card>
          </Box>
        )}
      </Box>

      <Box jc="fe">
        {!account.IsActive && (
          <Button colorScheme="primary" onClick={handleActivateClick}>
            {t('Activate.activate', { defaultValue: 'Activate' })}
          </Button>
        )}

        {account.IsActive && activeRow === '' && (
          <NoFillButton colorScheme="destructive" onClick={() => handleDialogue()}>
            {t('Deactivate.deactivate', { defaultValue: 'Deactivate' })}
          </NoFillButton>
        )}
      </Box>
    </div>
  );

  function formatPhoneNumber(phoneNumber: string): string {
    // Remove all non-numeric characters
    const cleaned = `${phoneNumber}`.replace(/\D/g, '');

    // Check the length of the cleaned number
    if (cleaned.length < 10 || cleaned.length > 12) {
      // If the length is invalid, return the original input
      return phoneNumber;
    }

    let countryCode = '1'; // Default country code
    let localNumber = cleaned;

    if (cleaned.length === 11) {
      // If the length is 11, assume the first digit is the country code

      countryCode = cleaned[0];
      localNumber = cleaned.slice(1);
    } else if (cleaned.length === 12) {
      // If the length is 12, assume the first two digits are the country code
      countryCode = cleaned.slice(0, 2);
      localNumber = cleaned.slice(2);
    }

    // Format the local number
    const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);

    if (match) {
      return `+${countryCode} (${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phoneNumber;
  }
}

type RowProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

function Row({ label, children, className }: RowProps) {
  return (
    <div data-testid={`detail-row-${label}`} className={cn('detailRow', className)}>
      <div className={cn('label')}>{label}</div>
      <div>{children}</div>
    </div>
  );
}
